import React, { useRef } from 'react';
import { BoldOutlined, ItalicOutlined, StrikethroughOutlined, CodeOutlined, TagOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { stateGlobal } from '../../../interfaces/Global';

export interface TextAreaDetalhamentoProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  style?: React.CSSProperties;
  maxLength?: number;
  showCount?: boolean;
}

type Formato = 'bold' | 'italic' | 'strike' | 'mono' | 'bullet';

const delimitadores: Record<Formato, [string, string]> = {
  bold: ['*', '*'],
  italic: ['_', '_'],
  strike: ['~', '~'],
  mono: ['```', '```'],
  bullet: ['• ', '']
};

export const TextAreaDetalhamento: React.FC<TextAreaDetalhamentoProps> = ({
  value = '',
  onChange,
  placeholder = 'Descreva as condições e cláusulas do detalhamento...',
  rows = 6,
  disabled = false,
  style,
  maxLength = 650,
  showCount = true,
}) => {
  const isDark = useSelector((state: stateGlobal) => state?.global?.theme === 'dark');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const aplicarFormatacao = (tipo: Formato) => {
    if (disabled) return;
    const textarea = textareaRef.current;
    if (!textarea) return;

    const inicio = textarea.selectionStart;
    const fim = textarea.selectionEnd;
    const [wStart, wEnd] = delimitadores[tipo];

    const currentText = value || '';
    const textoAntes = currentText.substring(0, inicio);
    const textoSelecionado = currentText.substring(inicio, fim);
    const textoDepois = currentText.substring(fim);

    const novoTexto = `${textoAntes}${wStart}${textoSelecionado}${wEnd}${textoDepois}`;
    
    if (maxLength && novoTexto.length > maxLength) {
      return;
    }

    if (onChange) {
      onChange(novoTexto);
    }

    setTimeout(() => {
      textarea.focus();
      if (inicio === fim) {
        const novaPosicao = inicio + wStart.length;
        textarea.setSelectionRange(novaPosicao, novaPosicao);
      } else {
        const novoFim = fim + wStart.length;
        textarea.setSelectionRange(inicio + wStart.length, novoFim);
      }
    }, 0);
  };

  const inserirTexto = (textoParaInserir: string) => {
    if (disabled) return;
    const textarea = textareaRef.current;
    if (!textarea) return;

    const inicio = textarea.selectionStart;
    const fim = textarea.selectionEnd;

    const currentText = value || '';
    const textoAntes = currentText.substring(0, inicio);
    const textoDepois = currentText.substring(fim);

    const novoTexto = `${textoAntes}${textoParaInserir}${textoDepois}`;
    
    if (maxLength && novoTexto.length > maxLength) {
      return;
    }

    if (onChange) {
      onChange(novoTexto);
    }

    setTimeout(() => {
      textarea.focus();
      const novaPosicao = inicio + textoParaInserir.length;
      textarea.setSelectionRange(novaPosicao, novaPosicao);
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (disabled) return;
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') {
      e.preventDefault();
      aplicarFormatacao('bold');
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'i') {
      e.preventDefault();
      aplicarFormatacao('italic');
    }
  };

  const tamanhoAtual = (value || '').length;
  const atingiuLimite = maxLength ? tamanhoAtual >= maxLength : false;

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '6px', ...style }}>
      {/* Barra de Ferramentas de Formatação */}
      <div 
        style={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: '6px', 
          background: isDark ? '#1e1e1e' : '#f8fafc', 
          padding: '6px 8px', 
          borderRadius: '6px',
          border: isDark ? '1px solid #303030' : '1px solid #e2e8f0',
          flexWrap: 'wrap'
        }}
      >
        <button
          type="button"
          onClick={() => aplicarFormatacao('bold')}
          disabled={disabled}
          style={{
            fontWeight: 'bold',
            padding: '3px 8px',
            fontSize: '12px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            background: isDark ? '#2a2a2a' : '#ffffff',
            border: isDark ? '1px solid #444444' : '1px solid #cbd5e1',
            borderRadius: '4px',
            color: isDark ? '#e2e8f0' : '#1e293b',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4
          }}
          title="Negrito (*texto*) - Ctrl+B"
        >
          <BoldOutlined /> Negrito
        </button>

        <button
          type="button"
          onClick={() => aplicarFormatacao('italic')}
          disabled={disabled}
          style={{
            fontStyle: 'italic',
            padding: '3px 8px',
            fontSize: '12px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            background: isDark ? '#2a2a2a' : '#ffffff',
            border: isDark ? '1px solid #444444' : '1px solid #cbd5e1',
            borderRadius: '4px',
            color: isDark ? '#e2e8f0' : '#1e293b',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4
          }}
          title="Itálico (_texto_) - Ctrl+I"
        >
          <ItalicOutlined /> Itálico
        </button>

        <button
          type="button"
          onClick={() => aplicarFormatacao('strike')}
          disabled={disabled}
          style={{
            textDecoration: 'line-through',
            padding: '3px 8px',
            fontSize: '12px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            background: isDark ? '#2a2a2a' : '#ffffff',
            border: isDark ? '1px solid #444444' : '1px solid #cbd5e1',
            borderRadius: '4px',
            color: isDark ? '#e2e8f0' : '#1e293b',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4
          }}
          title="Tachado (~texto~)"
        >
          <StrikethroughOutlined /> Tachado
        </button>

        <button
          type="button"
          onClick={() => aplicarFormatacao('mono')}
          disabled={disabled}
          style={{
            fontFamily: 'monospace',
            padding: '3px 8px',
            fontSize: '12px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            background: isDark ? '#2a2a2a' : '#ffffff',
            border: isDark ? '1px solid #444444' : '1px solid #cbd5e1',
            borderRadius: '4px',
            color: isDark ? '#e2e8f0' : '#1e293b',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4
          }}
          title="Monoespaçado (```texto```)"
        >
          <CodeOutlined /> Mono
        </button>

        <button
          type="button"
          onClick={() => aplicarFormatacao('bullet')}
          disabled={disabled}
          style={{
            padding: '3px 8px',
            fontSize: '12px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            background: isDark ? '#2a2a2a' : '#ffffff',
            border: isDark ? '1px solid #444444' : '1px solid #cbd5e1',
            borderRadius: '4px',
            color: isDark ? '#e2e8f0' : '#1e293b',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4
          }}
          title="Inserir Marcador (• Item)"
        >
          <UnorderedListOutlined /> Marcador (•)
        </button>

        <button
          type="button"
          onClick={() => inserirTexto('{{forma_pagamento}}')}
          disabled={disabled}
          style={{
            padding: '3px 10px',
            fontSize: '12px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            background: isDark ? 'rgba(14, 165, 233, 0.15)' : '#e0f2fe',
            border: isDark ? '1px solid #0284c7' : '1px solid #7dd3fc',
            borderRadius: '4px',
            color: isDark ? '#38bdf8' : '#0369a1',
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            marginLeft: 'auto'
          }}
          title="Inserir variável de forma de pagamento da proposta"
        >
          <TagOutlined /> Inserir &#123;&#123;forma_pagamento&#125;&#125;
        </button>
      </div>

      {/* Campo de Entrada Textarea */}
      <textarea
        ref={textareaRef}
        value={value || ''}
        disabled={disabled}
        maxLength={maxLength}
        onChange={(e) => onChange?.(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        style={{
          width: '100%',
          padding: '8px 10px',
          borderRadius: '6px',
          border: `1px solid ${atingiuLimite ? '#ef4444' : isDark ? '#303030' : '#d9d9d9'}`,
          fontSize: '13px',
          lineHeight: '1.45',
          fontFamily: 'inherit',
          resize: 'vertical',
          outline: 'none',
          transition: 'all 0.2s ease',
          background: disabled ? (isDark ? '#262626' : '#f5f5f5') : (isDark ? '#141414' : '#ffffff'),
          color: isDark ? '#ffffffd9' : '#1e293b'
        }}
      />

      {/* Contador de Caracteres */}
      {showCount && maxLength && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: '11px', color: atingiuLimite ? '#ef4444' : isDark ? '#8c8c8c' : '#64748b' }}>
          <span>{tamanhoAtual} / {maxLength} caracteres</span>
        </div>
      )}
    </div>
  );
};

export default TextAreaDetalhamento;